import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    Users, Search, Filter, RefreshCw, Loader, Crown, Zap,
    Building2, MapPin, CheckCircle, Eye, EyeOff,
    Trash2, Calendar, AlertTriangle, X,
    Send, Link2
} from 'lucide-react';
import { isAdminUser } from '../utils/adminClaim';

const AdminMatchingManagement = ({ userInfo }) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('labs'); // 'labs' | 'requests' | 'connections'
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterPlan, setFilterPlan] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');

    // 데이터
    const [matchingLabs, setMatchingLabs] = useState([]);
    const [connectionRequests, setConnectionRequests] = useState([]);

    // 모달
    const [selectedItem, setSelectedItem] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);

            // 매칭 등록 기공소 로드
            const labsSnapshot = await getDocs(collection(db, 'matchingLabs'));
            const labs = labsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                subscribedAt: doc.data().subscribedAt?.toDate?.() || null,
                expiresAt: doc.data().expiresAt?.toDate?.() || null
            }));
            setMatchingLabs(labs);

            // 연결 요청 로드
            const requestsSnapshot = await getDocs(collection(db, 'connectionRequests'));
            const requests = requestsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate?.() || null
            }));
            // 최신순 정렬
            requests.sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));
            setConnectionRequests(requests);

        } catch (error) {
            console.error('데이터 로드 실패:', error);
            alert('데이터를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!userInfo) {
            alert('로그인이 필요합니다.');
            navigate('/login');
            return;
        }
        const isAdmin = isAdminUser(userInfo);
        if (!isAdmin) {
            alert('관리자만 접근 가능합니다.');
            navigate('/dashboard');
            return;
        }
        loadData();
    }, [userInfo, navigate, loadData]);

    // 기공소 활성/비활성 토글
    const toggleLabStatus = async (lab) => {
        try {
            setActionLoading(lab.id);
            const newStatus = !lab.isActive;
            await updateDoc(doc(db, 'matchingLabs', lab.id), {
                isActive: newStatus,
                updatedAt: serverTimestamp()
            });
            setMatchingLabs(prev => prev.map(l =>
                l.id === lab.id ? { ...l, isActive: newStatus } : l
            ));
            alert(newStatus ? '활성화되었습니다.' : '비활성화되었습니다.');
        } catch (error) {
            console.error('상태 변경 실패:', error);
            alert('상태 변경에 실패했습니다.');
        } finally {
            setActionLoading(null);
        }
    };

    // 기공소 삭제
    const deleteLab = async () => {
        if (!selectedItem) return;
        try {
            setActionLoading(selectedItem.id);
            await deleteDoc(doc(db, 'matchingLabs', selectedItem.id));
            setMatchingLabs(prev => prev.filter(l => l.id !== selectedItem.id));
            setShowDeleteModal(false);
            setSelectedItem(null);
            alert('삭제되었습니다.');
        } catch (error) {
            console.error('삭제 실패:', error);
            alert('삭제에 실패했습니다.');
        } finally {
            setActionLoading(null);
        }
    };

    // 연결 요청 삭제
    const deleteRequest = async (requestId) => {
        if (!window.confirm('이 연결 요청을 삭제하시겠습니까?')) return;
        try {
            setActionLoading(requestId);
            await deleteDoc(doc(db, 'connectionRequests', requestId));
            setConnectionRequests(prev => prev.filter(r => r.id !== requestId));
            alert('삭제되었습니다.');
        } catch (error) {
            console.error('삭제 실패:', error);
            alert('삭제에 실패했습니다.');
        } finally {
            setActionLoading(null);
        }
    };

    // 필터링
    const filteredLabs = matchingLabs.filter(lab => {
        const matchesSearch = !searchQuery ||
            lab.labName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            lab.region?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            lab.email?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesPlan = filterPlan === 'all' || lab.plan === filterPlan;
        const matchesStatus = filterStatus === 'all' ||
            (filterStatus === 'active' && lab.isActive) ||
            (filterStatus === 'inactive' && !lab.isActive);
        return matchesSearch && matchesPlan && matchesStatus;
    });

    const filteredRequests = connectionRequests.filter(request => {
        const matchesSearch = !searchQuery ||
            request.clinicName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            request.labName?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = filterStatus === 'all' || request.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    // 통계
    const stats = {
        totalLabs: matchingLabs.length,
        activeLabs: matchingLabs.filter(l => l.isActive).length,
        premiumLabs: matchingLabs.filter(l => l.plan === 'premium').length,
        totalRequests: connectionRequests.length,
        pendingRequests: connectionRequests.filter(r => r.status === 'pending').length,
        acceptedRequests: connectionRequests.filter(r => r.status === 'accepted').length
    };

    const isExpired = (expiresAt) => {
        if (!expiresAt) return false;
        return new Date(expiresAt) < new Date();
    };

    const getDaysRemaining = (expiresAt) => {
        if (!expiresAt) return 0;
        const diff = new Date(expiresAt) - new Date();
        return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <Loader size={40} style={{ animation: 'spin 1s linear infinite' }} />
                <p>데이터를 불러오는 중...</p>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* 헤더 */}
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>
                        <Users size={28} />
                        매칭 서비스 관리
                    </h1>
                    <p style={styles.subtitle}>기공소 매칭 등록 및 연결 요청을 관리합니다</p>
                </div>
                <button style={styles.refreshBtn} onClick={loadData}>
                    <RefreshCw size={18} />
                    새로고침
                </button>
            </div>

            {/* 통계 */}
            <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                    <div style={{ ...styles.statIcon, backgroundColor: '#eef2ff' }}>
                        <Building2 size={24} color="#6366f1" />
                    </div>
                    <div>
                        <div style={styles.statValue}>{stats.totalLabs}</div>
                        <div style={styles.statLabel}>등록 기공소</div>
                    </div>
                </div>
                <div style={styles.statCard}>
                    <div style={{ ...styles.statIcon, backgroundColor: '#d1fae5' }}>
                        <CheckCircle size={24} color="#10b981" />
                    </div>
                    <div>
                        <div style={styles.statValue}>{stats.activeLabs}</div>
                        <div style={styles.statLabel}>활성 기공소</div>
                    </div>
                </div>
                <div style={styles.statCard}>
                    <div style={{ ...styles.statIcon, backgroundColor: '#fef3c7' }}>
                        <Crown size={24} color="#f59e0b" />
                    </div>
                    <div>
                        <div style={styles.statValue}>{stats.premiumLabs}</div>
                        <div style={styles.statLabel}>프리미엄</div>
                    </div>
                </div>
                <div style={styles.statCard}>
                    <div style={{ ...styles.statIcon, backgroundColor: '#fee2e2' }}>
                        <Send size={24} color="#ef4444" />
                    </div>
                    <div>
                        <div style={styles.statValue}>{stats.pendingRequests}</div>
                        <div style={styles.statLabel}>대기중 요청</div>
                    </div>
                </div>
            </div>

            {/* 탭 */}
            <div style={styles.tabs}>
                <button
                    onClick={() => setActiveTab('labs')}
                    style={{ ...styles.tab, ...(activeTab === 'labs' ? styles.tabActive : {}) }}
                >
                    <Building2 size={18} />
                    등록 기공소 ({matchingLabs.length})
                </button>
                <button
                    onClick={() => setActiveTab('requests')}
                    style={{ ...styles.tab, ...(activeTab === 'requests' ? styles.tabActive : {}) }}
                >
                    <Link2 size={18} />
                    연결 요청 ({connectionRequests.length})
                </button>
            </div>

            {/* 검색 & 필터 */}
            <div style={styles.filterSection}>
                <div style={styles.searchBox}>
                    <Search size={20} color="#64748b" />
                    <input
                        type="text"
                        placeholder="검색..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={styles.searchInput}
                    />
                </div>
                {activeTab === 'labs' && (
                    <>
                        <div style={styles.filterGroup}>
                            <Filter size={18} color="#64748b" />
                            <select
                                value={filterPlan}
                                onChange={(e) => setFilterPlan(e.target.value)}
                                style={styles.filterSelect}
                            >
                                <option value="all">전체 플랜</option>
                                <option value="basic">베이직</option>
                                <option value="premium">프리미엄</option>
                            </select>
                        </div>
                        <div style={styles.filterGroup}>
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                style={styles.filterSelect}
                            >
                                <option value="all">전체 상태</option>
                                <option value="active">활성</option>
                                <option value="inactive">비활성</option>
                            </select>
                        </div>
                    </>
                )}
                {activeTab === 'requests' && (
                    <div style={styles.filterGroup}>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            style={styles.filterSelect}
                        >
                            <option value="all">전체 상태</option>
                            <option value="pending">대기중</option>
                            <option value="accepted">수락됨</option>
                            <option value="rejected">거절됨</option>
                        </select>
                    </div>
                )}
            </div>

            {/* 등록 기공소 테이블 */}
            {activeTab === 'labs' && (
                <div style={styles.tableContainer}>
                    {filteredLabs.length === 0 ? (
                        <div style={styles.emptyState}>
                            <Building2 size={48} color="#cbd5e1" />
                            <p>등록된 기공소가 없습니다</p>
                        </div>
                    ) : (
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>기공소</th>
                                    <th style={styles.th}>플랜</th>
                                    <th style={styles.th}>지역</th>
                                    <th style={styles.th}>전문분야</th>
                                    <th style={styles.th}>만료일</th>
                                    <th style={styles.th}>상태</th>
                                    <th style={styles.th}>관리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredLabs.map(lab => (
                                    <tr key={lab.id} style={styles.tr}>
                                        <td style={styles.td}>
                                            <div style={styles.labCell}>
                                                <div style={styles.labIcon}>
                                                    <Building2 size={20} color="#6366f1" />
                                                </div>
                                                <div>
                                                    <div style={styles.labName}>{lab.labName}</div>
                                                    <div style={styles.labEmail}>{lab.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <span style={{
                                                ...styles.planBadge,
                                                backgroundColor: lab.plan === 'premium' ? '#fef3c7' : '#e0e7ff',
                                                color: lab.plan === 'premium' ? '#92400e' : '#4338ca'
                                            }}>
                                                {lab.plan === 'premium' ? <Crown size={12} /> : <Zap size={12} />}
                                                {lab.plan === 'premium' ? '프리미엄' : '베이직'}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={styles.regionCell}>
                                                <MapPin size={14} color="#64748b" />
                                                {lab.region || '-'}
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={styles.specialtiesCell}>
                                                {lab.specialties?.slice(0, 2).map((s, idx) => (
                                                    <span key={idx} style={styles.specialtyTag}>{s}</span>
                                                ))}
                                                {lab.specialties?.length > 2 && (
                                                    <span style={styles.moreTag}>+{lab.specialties.length - 2}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            {lab.expiresAt ? (
                                                <div style={{
                                                    ...styles.expiryCell,
                                                    color: isExpired(lab.expiresAt) ? '#dc2626' : '#64748b'
                                                }}>
                                                    <Calendar size={14} />
                                                    {isExpired(lab.expiresAt)
                                                        ? '만료됨'
                                                        : `${getDaysRemaining(lab.expiresAt)}일 남음`
                                                    }
                                                </div>
                                            ) : '-'}
                                        </td>
                                        <td style={styles.td}>
                                            <span style={{
                                                ...styles.statusBadge,
                                                backgroundColor: lab.isActive ? '#d1fae5' : '#fee2e2',
                                                color: lab.isActive ? '#065f46' : '#991b1b'
                                            }}>
                                                {lab.isActive ? '활성' : '비활성'}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={styles.actionButtons}>
                                                <button
                                                    style={{
                                                        ...styles.actionBtn,
                                                        backgroundColor: lab.isActive ? '#fee2e2' : '#d1fae5',
                                                        color: lab.isActive ? '#dc2626' : '#059669'
                                                    }}
                                                    onClick={() => toggleLabStatus(lab)}
                                                    disabled={actionLoading === lab.id}
                                                    title={lab.isActive ? '비활성화' : '활성화'}
                                                >
                                                    {actionLoading === lab.id
                                                        ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                                                        : lab.isActive ? <EyeOff size={16} /> : <Eye size={16} />
                                                    }
                                                </button>
                                                <button
                                                    style={{ ...styles.actionBtn, backgroundColor: '#fee2e2', color: '#dc2626' }}
                                                    onClick={() => { setSelectedItem(lab); setShowDeleteModal(true); }}
                                                    title="삭제"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* 연결 요청 테이블 */}
            {activeTab === 'requests' && (
                <div style={styles.tableContainer}>
                    {filteredRequests.length === 0 ? (
                        <div style={styles.emptyState}>
                            <Link2 size={48} color="#cbd5e1" />
                            <p>연결 요청이 없습니다</p>
                        </div>
                    ) : (
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>치과</th>
                                    <th style={styles.th}>기공소</th>
                                    <th style={styles.th}>메시지</th>
                                    <th style={styles.th}>요청일</th>
                                    <th style={styles.th}>상태</th>
                                    <th style={styles.th}>관리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRequests.map(request => (
                                    <tr key={request.id} style={styles.tr}>
                                        <td style={styles.td}>
                                            <div>
                                                <div style={styles.requestName}>{request.clinicName}</div>
                                                <div style={styles.requestEmail}>{request.clinicEmail}</div>
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={styles.requestName}>{request.labName}</div>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={styles.messageCell}>
                                                {request.message || '-'}
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            {request.createdAt?.toLocaleDateString('ko-KR') || '-'}
                                        </td>
                                        <td style={styles.td}>
                                            <span style={{
                                                ...styles.statusBadge,
                                                backgroundColor:
                                                    request.status === 'accepted' ? '#d1fae5' :
                                                        request.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                                                color:
                                                    request.status === 'accepted' ? '#065f46' :
                                                        request.status === 'rejected' ? '#991b1b' : '#92400e'
                                            }}>
                                                {request.status === 'accepted' ? '수락됨' :
                                                    request.status === 'rejected' ? '거절됨' : '대기중'}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            <button
                                                style={{ ...styles.actionBtn, backgroundColor: '#fee2e2', color: '#dc2626' }}
                                                onClick={() => deleteRequest(request.id)}
                                                disabled={actionLoading === request.id}
                                                title="삭제"
                                            >
                                                {actionLoading === request.id
                                                    ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                                                    : <Trash2 size={16} />
                                                }
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* 삭제 확인 모달 */}
            {showDeleteModal && selectedItem && (
                <div style={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
                    <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <div style={styles.modalIcon}>
                            <AlertTriangle size={48} color="#dc2626" />
                        </div>
                        <h3 style={styles.modalTitle}>기공소 삭제</h3>
                        <p style={styles.modalText}>
                            <strong>{selectedItem.labName}</strong>을(를) 매칭 목록에서 삭제하시겠습니까?
                        </p>
                        <div style={styles.modalWarning}>
                            <AlertTriangle size={16} />
                            <span>이 작업은 되돌릴 수 없습니다.</span>
                        </div>
                        <div style={styles.modalButtons}>
                            <button style={styles.cancelBtn} onClick={() => setShowDeleteModal(false)}>
                                <X size={18} /> 취소
                            </button>
                            <button
                                style={styles.deleteBtn}
                                onClick={deleteLab}
                                disabled={actionLoading === selectedItem.id}
                            >
                                {actionLoading === selectedItem.id
                                    ? <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                                    : <Trash2 size={18} />
                                }
                                삭제
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: { padding: '24px', maxWidth: '1400px', margin: '0 auto' },
    loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '400px', gap: '16px', color: '#64748b' },

    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
    title: { display: 'flex', alignItems: 'center', gap: '12px', fontSize: '28px', fontWeight: '700', color: '#1e293b', margin: 0 },
    subtitle: { fontSize: '14px', color: '#64748b', margin: '8px 0 0 0' },
    refreshBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },

    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' },
    statCard: { display: 'flex', alignItems: 'center', gap: '16px', padding: '20px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    statIcon: { width: '48px', height: '48px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    statValue: { fontSize: '24px', fontWeight: '700', color: '#1e293b' },
    statLabel: { fontSize: '13px', color: '#64748b' },

    tabs: { display: 'flex', gap: '8px', marginBottom: '20px' },
    tab: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', backgroundColor: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    tabActive: { backgroundColor: '#6366f1', color: '#fff' },

    filterSection: { display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' },
    searchBox: { flex: 1, minWidth: '250px', display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '10px' },
    searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: '14px' },
    filterGroup: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px', backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '10px' },
    filterSelect: { border: 'none', outline: 'none', fontSize: '14px', backgroundColor: 'transparent', cursor: 'pointer' },

    tableContainer: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', minWidth: '900px' },
    th: { padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
    tr: { borderBottom: '1px solid #f1f5f9' },
    td: { padding: '14px 16px', fontSize: '14px', color: '#1e293b', verticalAlign: 'middle' },

    labCell: { display: 'flex', alignItems: 'center', gap: '12px' },
    labIcon: { width: '40px', height: '40px', backgroundColor: '#eef2ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    labName: { fontWeight: '600', color: '#1e293b' },
    labEmail: { fontSize: '12px', color: '#94a3b8' },

    planBadge: { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' },

    regionCell: { display: 'flex', alignItems: 'center', gap: '6px' },

    specialtiesCell: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
    specialtyTag: { padding: '2px 8px', backgroundColor: '#e0e7ff', color: '#4338ca', borderRadius: '4px', fontSize: '11px' },
    moreTag: { padding: '2px 8px', backgroundColor: '#f1f5f9', color: '#64748b', borderRadius: '4px', fontSize: '11px' },

    expiryCell: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' },

    statusBadge: { display: 'inline-block', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },

    actionButtons: { display: 'flex', gap: '8px' },
    actionBtn: { width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: '6px', cursor: 'pointer' },

    requestName: { fontWeight: '600', color: '#1e293b' },
    requestEmail: { fontSize: '12px', color: '#94a3b8' },
    messageCell: { maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#64748b', fontSize: '13px' },

    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', color: '#94a3b8', gap: '12px' },

    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: 'white', borderRadius: '16px', padding: '32px', maxWidth: '400px', width: '90%', textAlign: 'center' },
    modalIcon: { marginBottom: '16px' },
    modalTitle: { fontSize: '20px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' },
    modalText: { fontSize: '15px', color: '#64748b', marginBottom: '16px' },
    modalWarning: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', backgroundColor: '#fef3c7', borderRadius: '8px', fontSize: '13px', color: '#92400e', marginBottom: '24px' },
    modalButtons: { display: 'flex', gap: '12px', justifyContent: 'center' },
    cancelBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', backgroundColor: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    deleteBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }
};

export default AdminMatchingManagement;
